import type { FC, ReactNode } from 'react'

import { useEffect, useRef } from 'react'

interface ReadingProgressProps {
    targetId: string
}

/**
 * A 1px blue line under the navigation that fills as the article is read, with a mono readout.
 */
export const ReadingProgress: FC<ReadingProgressProps> = ({ targetId }): ReactNode => {
    const fillRef = useRef<HTMLSpanElement>(null)
    const labelRef = useRef<HTMLSpanElement>(null)

    useEffect(() => {
        let raf = 0

        const update = (): void => {
            const target = document.getElementById(targetId)

            if (!target) return

            const rect = target.getBoundingClientRect()
            const distance = Math.max(1, rect.height - window.innerHeight * 0.55)
            const progress = Math.min(1, Math.max(0, (window.innerHeight * 0.3 - rect.top) / distance))

            if (fillRef.current) fillRef.current.style.transform = `scaleX(${progress.toFixed(4)})`
            if (labelRef.current) labelRef.current.textContent = `Read ${String(Math.round(progress * 100)).padStart(3, '0')}%`
        }

        const schedule = (): void => {
            cancelAnimationFrame(raf)
            raf = requestAnimationFrame(update)
        }

        update()
        window.addEventListener('scroll', schedule, { passive: true })
        window.addEventListener('resize', schedule)

        return () => {
            cancelAnimationFrame(raf)
            window.removeEventListener('scroll', schedule)
            window.removeEventListener('resize', schedule)
        }
    }, [targetId])

    return (
        <div className='follow-header pointer-events-none fixed inset-x-0 top-14 z-30 desk:top-16' aria-hidden='true'>
            <div className='relative h-px bg-hairline'>
                <span ref={fillRef} className='absolute inset-0 origin-left bg-signal' style={{ transform: 'scaleX(0)' }} />
            </div>

            <span ref={labelRef} className='hud absolute right-gutter top-2 border border-hairline bg-background px-2 py-0.5 text-mute'>
                Read 000%
            </span>
        </div>
    )
}
